import { useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '../components/Icon'
import { LogoMark } from '../components/Logo'
import { useAuth } from '../auth/AuthContext'
import { useCatalog } from '../hooks/useCatalog'
import { listOrders, fetchOrderUsage, type OrderDetail, type OrderUsage } from '../api/orders'
import {
  fetchCreditBalance,
  fetchCreditHistory,
  formatDollars,
  type CreditBalance,
  type CreditHistoryRow,
} from '../api/credits'
import { ReferralWidget } from '../components/ReferralWidget'
import {
  COUNTRIES,
  REGIONAL_PLANS_META,
  formatData,
  priceDollars,
  type Plan,
} from '../data/catalog'
import { languageFromPath, pathInLanguage, dateLocaleFor, type SupportedLanguage } from '../i18n/i18n'
import { useDocumentMeta } from '../i18n/useDocumentMeta'

type Tab = 'esims' | 'orders' | 'support'
type DestMeta = { code: string; name: string; flag: string; isRegional: boolean }
type TFunc = (key: string, opts?: Record<string, unknown>) => string

const TAB_IDS: Tab[] = ['esims', 'orders', 'support']

function localizedMetaName(tCommon: TFunc, m: { code: string; name: string; isRegional?: boolean }): string {
  const ns = m.isRegional ? 'regions' : 'countries'
  const v = tCommon(`${ns}.${m.code}`)
  return v === `${ns}.${m.code}` ? m.name : v
}

function localizedReason(t: TFunc, reason: string): string {
  const v = t(`credit.reason.${reason}`)
  return v === `credit.reason.${reason}` ? reason : v
}

export default function Account() {
  const { user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { plans } = useCatalog()
  const { t } = useTranslation('account')
  const { t: tCommon } = useTranslation('common')
  const lang = languageFromPath(location.pathname)
  useDocumentMeta({ title: t('seo.title'), noindex: true })
  const withLang = (p: string) => pathInLanguage(p, lang)
  const [tab, setTab] = useState<Tab>('esims')

  const [orders, setOrders] = useState<OrderDetail[] | null>(null)
  const [loadingOrders, setLoadingOrders] = useState(false)
  const [ordersError, setOrdersError] = useState<string | null>(null)

  // Credit balance is shown in the head pill + the Credit tab. We fetch
  // it on every account-page mount so the value reflects whatever just
  // happened on a checkout (earned / spent / refunded). Cheap query.
  const [credit, setCredit] = useState<CreditBalance | null>(null)

  useEffect(() => {
    if (!user) return
    let cancelled = false
    setLoadingOrders(true)
    listOrders(1, 50)
      .then((res) => {
        if (!cancelled) setOrders(res.orders)
      })
      .catch((e: Error) => {
        if (!cancelled) setOrdersError(e.message)
      })
      .finally(() => {
        if (!cancelled) setLoadingOrders(false)
      })
    fetchCreditBalance()
      .then((b) => !cancelled && setCredit(b))
      .catch(() => {})  // balance failure is non-blocking; pill just hides
    return () => {
      cancelled = true
    }
  }, [user])

  const planById = useMemo(() => {
    const m = new Map<string, Plan>()
    if (plans) plans.forEach((p) => m.set(p.id, p))
    return m
  }, [plans])

  if (authLoading) return <div className="page-stub">{t('loading')}</div>
  if (!user) return <Navigate to={withLang('/login')} state={{ from: withLang('/account') }} replace />

  const activeOrders = orders?.filter((o) => o.status === 'delivered') ?? []

  // "Claimed via magic-link only" state — user has no password and no
  // Google link. Render a one-shot banner inviting them to connect Google.
  // Once either credential is set, the banner disappears for good.
  const needsCredential = user.has_password === false && user.has_google === false

  return (
    <div className="account">
      <div className="account-head">
        <div>
          <h1>{t('head.greeting', { name: user.name || user.email.split('@')[0] })}</h1>
          <div className="sub">
            {orders === null
              ? t('head.loadingEsims')
              : t('head.ordersSummary', { active: activeOrders.length, total: orders.length })}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn primary sm" onClick={() => navigate(withLang('/destinations'))}>
            {t('head.newEsim')}
          </button>
        </div>
      </div>

      {/* Promoted credit strip — sits between the welcome and the tab
          strip, shown only when there's a positive balance. Same dark-
          navy gradient + italic serif typography as the credit hero
          card on the Credit tab itself, so the visual language stays
          consistent across surfaces. Designed to be unmissable: full
          content-width, sits in the customer's reading path before
          they get to the eSIM list. */}
      {credit && credit.balance_cents > 0 && (
        <button
          type="button"
          className="credit-strip"
          onClick={() => navigate(withLang('/destinations'))}
          aria-label={t('credit.strip.aria')}
        >
          <div className="credit-strip__left">
            <div className="credit-strip__mark"><LogoMark size={20} /></div>
            <div>
              <div className="credit-strip__eyebrow">{t('credit.strip.eyebrow')}</div>
              <div className="credit-strip__amount">
                {formatDollars(credit.balance_cents)}
              </div>
            </div>
          </div>
          <div className="credit-strip__right">
            <div className="credit-strip__copy">
              {t('credit.strip.copy')}
              {credit.earliest_expiry && t('credit.strip.expiryNote', {
                date: new Date(credit.earliest_expiry).toLocaleDateString(dateLocaleFor(lang), {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                }),
              })}
            </div>
            <span className="credit-strip__cta">
              {t('credit.strip.cta')} <Icon name="arrow" size={14} />
            </span>
          </div>
        </button>
      )}

      <ReferralWidget />

      {needsCredential && (
        <div className="claim-banner">
          <div>
            <div className="claim-banner__eyebrow">{t('claim.eyebrow')}</div>
            <p className="claim-banner__body">{t('claim.body')}</p>
          </div>
          <div className="claim-banner__actions">
            {/* No "Add a password": /signup refuses existing emails (it can't
                prove the inbox), and Login has no password field anyway. */}
            <a href="/api/auth/google" className="btn primary sm">{t('claim.connectGoogle')}</a>
          </div>
        </div>
      )}

      <div className="account-tabs">
        {TAB_IDS.map((id) => (
          <button key={id} className={tab === id ? 'active' : ''} onClick={() => setTab(id)}>
            {t(`tabs.${id}`)}
          </button>
        ))}
      </div>

      {ordersError && (
        <div
          style={{
            padding: 16,
            background: '#FFECE7',
            color: 'var(--pop)',
            borderRadius: 10,
            fontSize: 14,
            marginBottom: 16,
          }}
        >
          {ordersError}
        </div>
      )}

      {tab === 'esims' && (
        <EsimListTab
          orders={orders ?? []}
          planById={planById}
          loading={loadingOrders}
          onInstall={(ref) => navigate(withLang(`/order/${ref}`))}
          t={t}
          tCommon={tCommon}
          withLang={withLang}
          lang={lang}
        />
      )}

      {tab === 'orders' && (
        <OrdersTab
          orders={orders ?? []}
          planById={planById}
          loading={loadingOrders}
          t={t}
          tCommon={tCommon}
          lang={lang}
        />
      )}

      {tab === 'support' && <SupportTab t={t} />}
    </div>
  )
}

function EsimListTab({
  orders,
  planById,
  loading,
  onInstall,
  t,
  tCommon,
  withLang,
  lang,
}: {
  orders: OrderDetail[]
  planById: Map<string, Plan>
  loading: boolean
  onInstall: (ref: string) => void
  t: TFunc
  tCommon: TFunc
  withLang: (p: string) => string
  lang: SupportedLanguage
}) {
  if (loading && orders.length === 0) {
    return <div className="page-stub">{t('esimList.loading')}</div>
  }
  if (orders.length === 0) {
    return (
      <div
        style={{
          padding: 48,
          background: 'var(--bg-elev)',
          border: '1px solid var(--line)',
          borderRadius: 14,
          textAlign: 'center',
        }}
      >
        <h3 style={{ marginBottom: 8 }}>{t('esimList.emptyTitle')}</h3>
        <p className="muted" style={{ marginBottom: 20 }}>
          {t('esimList.emptyBody')}
        </p>
        <Link to={withLang('/destinations')} className="btn primary reset" style={{ padding: '12px 20px' }}>
          {t('esimList.browse')}
        </Link>
      </div>
    )
  }

  return (
    <div className="esim-list">
      {orders.map((o) => {
        const plan = planById.get(o.plan_id) ?? null
        const dest = plan ? resolveMeta(plan) : null
        return (
          <EsimCard
            key={o.reference}
            order={o}
            plan={plan}
            dest={dest}
            onInstall={() => onInstall(o.reference)}
            t={t}
            tCommon={tCommon}
            lang={lang}
          />
        )
      })}
    </div>
  )
}

// One card per order. Wraps the existing top row + a usage block below
// for delivered orders. Usage is fetched on mount (lazy — only when the
// card is rendered, since the My eSIMs tab is the default tab) plus on
// manual refresh. We don't poll: JoyTel updates with multi-minute lag
// anyway and each call hits a paid carrier API.
function EsimCard({
  order,
  plan,
  dest,
  onInstall,
  t,
  tCommon,
  lang,
}: {
  order: OrderDetail
  plan: Plan | null
  dest: DestMeta | null
  onInstall: () => void
  t: TFunc
  tCommon: TFunc
  lang: SupportedLanguage
}) {
  const flag = dest?.flag ?? '🌐'
  const name = dest ? localizedMetaName(tCommon, dest) : order.plan_id
  const isDelivered = order.status === 'delivered'

  // Usage state lives on the card so both the top-row status and the
  // usage panel below render from the same source of truth. Without
  // this lift, the row could show "Ready to install" while the panel
  // says "Expired" — contradictory and confusing.
  const [usage, setUsage] = useState<OrderUsage | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null)

  const loadUsage = async () => {
    setLoading(true)
    setError(null)
    try {
      const u = await fetchOrderUsage(order.reference)
      setUsage(u)
      setRefreshedAt(new Date())
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!isDelivered) return
    void loadUsage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.reference, isDelivered])

  // Derive the top-row status from the merge of DB status + live JoyTel
  // signals. JoyTel data wins when present; falls back to DB status.
  const display = displayStatus(t, order.status, usage)

  return (
    <div className="esim-card">
      <div className="esim-item esim-item--compact">
        <div className="flag">{flag}</div>
        <div>
          <div className="name">{t('esimCard.name', { name })}</div>
          <div className="plan">
            {plan
              ? t('esimCard.planLine', { data: formatData(plan.data_gb), days: plan.validity_days })
              : t('esimCard.planPending')} · {order.reference}
          </div>
        </div>
        <div className={`status ${display.cls}`}>
          <span className="dot"></span>
          {display.label}
        </div>
        <ValidityDate order={order} usage={usage} lang={lang} t={t} />
        <div style={{ display: 'flex', gap: 6 }}>
          {isDelivered ? (
            <button className="btn subtle sm" onClick={onInstall}>
              {t('esimCard.viewQr')}
            </button>
          ) : order.status === 'failed' ? (
            <button className="btn ghost sm" onClick={onInstall}>
              {t('esimCard.details')}
            </button>
          ) : (
            <button className="btn primary sm" onClick={onInstall}>
              {t('esimCard.track')}
            </button>
          )}
        </div>
      </div>
      {isDelivered && plan && (
        <UsagePanel
          planTotalGb={plan.data_gb}
          usage={usage}
          loading={loading}
          error={error}
          refreshedAt={refreshedAt}
          onRefresh={() => void loadUsage()}
          t={t}
          lang={lang}
        />
      )}
    </div>
  )
}

// Right-hand mono date slot on the eSIM card. JoyTel gives us a
// validity window in two flavors, and we render both — captioned so
// the meaning is unambiguous:
//   - Pre-activation (state=unused): the window is the wallet's
//     use-by deadline (often 6 months out, NOT the plan length).
//     Captioned "Activate within" so users don't read it as the
//     plan duration.
//   - Post-activation (state=active/depleted/expired): the window
//     IS the plan's actual run. Captioned "Plan window".
// If we don't have usage data yet, fall back to the order's purchase
// date with no caption.
function ValidityDate({
  order,
  usage,
  lang,
  t,
}: {
  order: OrderDetail
  usage: OrderUsage | null
  lang: SupportedLanguage
  t: TFunc
}) {
  const locale = dateLocaleFor(lang)
  const fallback = new Date(order.created_at).toLocaleDateString(locale)

  let rangeText: string | null = null
  let titleText: string | undefined
  if (usage?.activated_at && usage?.expires_at) {
    const start = new Date(usage.activated_at)
    const end = new Date(usage.expires_at)
    if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
      // Start: date only — the exact start moment is informational. End:
      // date + time + TZ — that's the deadline that actually matters
      // (activation cutoff pre-activation, data shut-off post). Both
      // render in the browser's local timezone via the Intl API.
      const startStr = start.toLocaleDateString(locale)
      const endStr = end.toLocaleString(locale, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      })
      rangeText = `${startStr} → ${endStr}`
      // Hover shows the full precision in local time for both endpoints.
      const fullOpts: Intl.DateTimeFormatOptions = {
        dateStyle: 'full',
        timeStyle: 'long',
      }
      titleText = `${start.toLocaleString(locale, fullOpts)} → ${end.toLocaleString(locale, fullOpts)}`
    }
  }

  if (!rangeText) {
    return (
      <div className="mono muted" style={{ fontSize: 12 }}>
        {fallback}
      </div>
    )
  }

  // Only a started plan is counting down. Deriving this from `state` used to
  // treat an installed-but-never-connected eSIM as ticking, which captioned
  // the connect-by deadline as if it were the plan window.
  const planTicking = usage?.plan_started === true || usage?.state === 'expired'
  const caption = planTicking
    ? t('esimCard.validity.planWindow')
    : t('esimCard.validity.activateWithin')

  return (
    <div
      title={titleText}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.3 }}
    >
      <span className="mono muted" style={{ fontSize: 12 }}>{rangeText}</span>
      <span style={{ fontSize: 10.5, color: 'var(--ink-3)', fontStyle: 'italic' }}>{caption}</span>
    </div>
  )
}

// Pick the right status label + class given the DB status and the
// live JoyTel data. JoyTel state wins when it tells us something
// definitive (expired / depleted / active), otherwise we fall back
// to the DB humanStatus mapping.
function displayStatus(
  t: TFunc,
  dbStatus: string,
  usage: OrderUsage | null,
): { label: string; cls: 'active' | 'inactive' | 'expired' } {
  if (usage) {
    if (usage.state === 'expired') return { label: t('installBadge.expired'), cls: 'expired' }
    if (usage.state === 'depleted') return { label: t('installBadge.depleted'), cls: 'expired' }
    if (usage.state === 'active') return { label: t('installBadge.active'), cls: 'active' }
  }
  return { label: humanStatus(t, dbStatus), cls: uiStatusClass(dbStatus) }
}

// Usage panel — sits below the eSIM row inside the same card. Renders
// one of three states: loading, error, or a progress bar with stats.
// State lives on the parent EsimCard so the top-row status badge and
// this panel render from the same data; we just consume props here.
function UsagePanel({
  planTotalGb,
  usage,
  loading,
  error,
  refreshedAt,
  onRefresh,
  t,
  lang,
}: {
  planTotalGb: number
  usage: OrderUsage | null
  loading: boolean
  error: string | null
  refreshedAt: Date | null
  onRefresh: () => void
  t: TFunc
  lang: SupportedLanguage
}) {
  // Plan total comes from our catalog (authoritative for what was sold).
  // Carrier-reported total can drift from this for various reasons; we
  // prefer the catalog total in the UI but show carrier-reported numbers
  // for "used" since that's the live data we care about.
  const planTotalMb = Math.round(planTotalGb * 1024)
  const usedMb = usage?.used_mb ?? null
  const percent =
    usedMb !== null && planTotalMb > 0
      ? Math.min(Math.round((usedMb / planTotalMb) * 1000) / 10, 100)
      : null
  const usedDisplay = formatMb(usedMb)
  const totalDisplay = formatMb(planTotalMb)

  const locale = dateLocaleFor(lang)
  const timeStr = refreshedAt
    ? refreshedAt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
    : ''

  return (
    <div className="usage-panel">
      <div className="usage-panel__head">
        <span className="usage-panel__label">
          {t('usage.label')}
          {usage && <InstallBadge usage={usage} t={t} />}
        </span>
        <button
          className="usage-panel__refresh"
          onClick={onRefresh}
          disabled={loading}
          aria-label={t('usage.refreshAria')}
          title={t('usage.refreshTitle')}
        >
          <Icon name="arrow-r" size={12} />
          {loading ? t('usage.refreshing') : t('usage.refresh')}
        </button>
      </div>

      {loading && !usage && (
        <div className="usage-panel__skeleton" aria-hidden="true">
          <div className="usage-panel__bar"><div className="usage-panel__fill" style={{ width: 0 }} /></div>
        </div>
      )}

      {error && !loading && (
        <div className="usage-panel__error">
          {t('usage.errorBody')} <button onClick={onRefresh}>{t('usage.tryAgain')}</button>
        </div>
      )}

      {!loading && !error && usage && (
        <>
          <div className="usage-panel__stats">
            <span className="usage-panel__used">
              {t('usage.stats', { used: usedDisplay, total: totalDisplay })}
            </span>
            {percent !== null && (
              <span className="usage-panel__percent mono">{percent.toFixed(percent < 10 ? 1 : 0)}%</span>
            )}
          </div>
          <div className="usage-panel__bar">
            <div
              className={`usage-panel__fill ${percent !== null && percent >= 85 ? 'usage-panel__fill--hot' : ''}`}
              style={{ width: `${percent ?? 0}%` }}
            />
          </div>
          <div className="usage-panel__meta mono">
            {usage.state === 'unused' && t('usage.stateUnused')}
            {usage.state === 'active' && refreshedAt && t('usage.updated', { time: timeStr })}
            {usage.state === 'depleted' && t('usage.stateDepleted')}
            {usage.state === 'expired' && t('usage.stateExpired')}
            {usage.state === 'unknown' && refreshedAt && t('usage.updated', { time: timeStr })}
          </div>
        </>
      )}
    </div>
  )
}

// Install-state badge derived from whichever signals are available.
// Priority order: explicit "expired" → push install events (more
// authoritative) → live JoyTel status query (always available) →
// usage-derived inference. Falls back gracefully to "Ready to install"
// when no signal points either way, so a customer who actually has
// installed the eSIM never sees a blatantly wrong "Not installed" badge.
function InstallBadge({ usage, t }: { usage: OrderUsage; t: TFunc }) {
  let label: string
  let cls: string

  const hasInstallEvent = !!usage.installed_at || !!usage.enabled_at
  const liveExpired = usage.esim_status === 'expired'

  // "Active" is reserved for a plan that is actually running. Switching the
  // profile on doesn't do that — only connecting does — so an eSIM sitting
  // installed on a phone before departure reads "Installed", which is both
  // true and consistent with the "activate within" dates beside it.
  if (usage.state === 'expired' || liveExpired) {
    label = t('installBadge.expired')
    cls = 'usage-badge--expired'
  } else if (usage.state === 'depleted') {
    label = t('installBadge.depleted')
    cls = 'usage-badge--depleted'
  } else if (usage.plan_started) {
    label = t('installBadge.active')
    cls = 'usage-badge--active'
  } else if (hasInstallEvent) {
    label = t('installBadge.installed')
    cls = 'usage-badge--active'
  } else {
    label = t('installBadge.ready')
    cls = 'usage-badge--ready'
  }

  return <span className={`usage-badge ${cls}`}>{label}</span>
}

function formatMb(mb: number | null): string {
  if (mb === null) return '—'
  if (mb >= 1024) return `${(mb / 1024).toFixed(mb >= 10240 ? 1 : 2)} GB`
  return `${mb} MB`
}

function OrdersTab({
  orders,
  planById,
  loading,
  t,
  tCommon,
  lang,
}: {
  orders: OrderDetail[]
  planById: Map<string, Plan>
  loading: boolean
  t: TFunc
  tCommon: TFunc
  lang: SupportedLanguage
}) {
  // Pull the credit ledger so each order row can show the credit it
  // earned (and any credit spent on it). Non-order events (admin
  // grants, expirations) live in the "Credit activity" section
  // rendered below the orders list.
  const [creditRows, setCreditRows] = useState<CreditHistoryRow[] | null>(null)
  useEffect(() => {
    fetchCreditHistory(1, 100)
      .then((h) => setCreditRows(h.rows))
      .catch(() => setCreditRows([]))
  }, [])

  // Group ledger rows by order reference so order rows can render
  // a per-order credit summary inline.
  const creditByOrder = (creditRows ?? []).reduce<Record<string, CreditHistoryRow[]>>(
    (acc, row) => {
      if (!row.related_order_reference) return acc
      const ref = row.related_order_reference
      acc[ref] = acc[ref] || []
      acc[ref].push(row)
      return acc
    },
    {},
  )
  const nonOrderEvents = (creditRows ?? []).filter((r) => !r.related_order_reference)

  const locale = dateLocaleFor(lang)

  if (loading && orders.length === 0) return <div className="page-stub">{t('ordersTab.loading')}</div>
  if (orders.length === 0) {
    return (
      <div
        style={{
          padding: 40,
          background: 'var(--bg-elev)',
          border: '1px solid var(--line)',
          borderRadius: 14,
          color: 'var(--ink-3)',
          textAlign: 'center',
        }}
      >
        {t('ordersTab.empty')}
      </div>
    )
  }
  return (
    <>
      <div className="esim-list">
        {orders.map((o) => {
          const plan = planById.get(o.plan_id) ?? null
          const dest = plan ? resolveMeta(plan) : null
          const flag = dest?.flag ?? '🌐'
          const name = dest ? localizedMetaName(tCommon, dest) : o.plan_id
          // Sum credit deltas associated with this order: positive
          // sum = net credit accrued (earned - reversed); negative =
          // net spent. We render whichever side is non-zero.
          const events = creditByOrder[o.reference] || []
          const earned = events
            .filter((e) => e.reason === 'order_earned' || e.reason === 'order_refunded_spend')
            .reduce((s, e) => s + e.delta_cents, 0)
          const spent = events
            .filter((e) => e.reason === 'order_spent')
            .reduce((s, e) => s + Math.abs(e.delta_cents), 0)
          const reversed = events
            .filter((e) => e.reason === 'order_refunded')
            .reduce((s, e) => s + Math.abs(e.delta_cents), 0)

          return (
            <div key={o.reference} className="esim-item">
              <div className="flag">{flag}</div>
              <div>
                <div className="name">{t('esimCard.name', { name })}</div>
                <div className="plan mono">
                  {o.reference} · {new Date(o.created_at).toLocaleDateString(locale)}
                </div>
              </div>
              <div className="mono muted" style={{ fontSize: 13 }}>
                {plan ? t('ordersTab.planLine', { data: formatData(plan.data_gb), days: plan.validity_days }) : '—'}
              </div>
              <div>
                <div className="mono" style={{ fontSize: 14 }}>
                  ${priceDollars(o.amount_cents)}
                </div>
                {(earned > 0 || spent > 0 || reversed > 0) && (
                  <div className="order-credit-note mono">
                    {earned > 0 && !reversed && t('credit.earnedTag', { amount: formatDollars(earned) })}
                    {spent > 0 && (
                      <>
                        {earned > 0 && ' · '}
                        {t('credit.spentTag', { amount: formatDollars(spent) })}
                      </>
                    )}
                    {reversed > 0 && t('credit.reversedTag', { amount: formatDollars(reversed) })}
                  </div>
                )}
              </div>
              <div>
                <span className={`badge ${o.status === 'delivered' ? 'ok' : o.status === 'failed' ? 'pop' : ''}`}>
                  {badgeLabel(t, o.status)}
                </span>
              </div>
              <div />
            </div>
          )
        })}
      </div>

      {/* Credit activity for events that don't tie back to a specific
          order — admin grants, expirations. Hidden when there are
          none (the per-order credit is already inline above). */}
      {nonOrderEvents.length > 0 && (
        <>
          <h3 style={{ fontSize: 15, fontWeight: 500, margin: '36px 0 12px', letterSpacing: '-0.01em' }}>
            {t('credit.otherActivity')}
          </h3>
          <div className="credit-rows">
            {nonOrderEvents.map((row) => (
              <div key={row.id} className="credit-row">
                <div>
                  <div className="credit-row__label">{localizedReason(t, row.reason)}</div>
                  <div className="credit-row__sub mono">
                    {new Date(row.created_at).toLocaleDateString(locale)}
                  </div>
                </div>
                <div className={`credit-row__amount ${row.delta_cents >= 0 ? 'pos' : 'neg'}`}>
                  {row.delta_cents >= 0 ? '+' : ''}
                  {formatDollars(row.delta_cents)}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  )
}

function SupportTab({ t }: { t: TFunc }) {
  return (
    <div
      style={{
        padding: 40,
        background: 'var(--bg-elev)',
        border: '1px solid var(--line)',
        borderRadius: 14,
        textAlign: 'center',
      }}
    >
      <h3>{t('support.title')}</h3>
      <p className="muted" style={{ marginTop: 8 }}>
        {t('support.body')}
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20 }}>
        <a
          href="mailto:support@nimvoy.com"
          className="btn primary reset"
          style={{ padding: '12px 20px' }}
        >
          <Icon name="arrow" size={14} /> support@nimvoy.com
        </a>
      </div>
    </div>
  )
}

function uiStatusClass(status: string): 'active' | 'inactive' | 'expired' {
  if (status === 'delivered') return 'active'
  if (status === 'failed' || status === 'payment_failed') return 'expired'
  return 'inactive'
}

function humanStatus(t: TFunc, status: string): string {
  // Same collapse-to-Processing rule as the customer order page.
  switch (status) {
    case 'delivered':
      return t('status.delivered')
    case 'failed':
      return t('status.failed')
    case 'payment_failed':
      return t('status.paymentFailed')
    case 'refunded':
      return t('status.refunded')
    case 'created':
      return t('status.created')
    case 'payment_received':
    case 'ordering':
    case 'qr_pending':
      return t('status.processing')
    default:
      return status
  }
}

function badgeLabel(t: TFunc, status: string): string {
  if (status === 'delivered') return t('badge.paid')
  if (status === 'failed') return t('badge.failed')
  if (status === 'payment_failed') return t('badge.paymentFailed')
  if (status === 'refunded') return t('badge.refunded')
  return status.toUpperCase()
}

function resolveMeta(plan: Plan): DestMeta | null {
  if (plan.plan_type === 'regional') {
    const r = REGIONAL_PLANS_META.find((x) => x.code === plan.country)
    return r ? { code: r.code, name: r.name, flag: r.flag, isRegional: true } : null
  }
  const c = COUNTRIES.find((x) => x.code === plan.country)
  return c ? { code: c.code, name: c.name, flag: c.flag, isRegional: false } : null
}
